import type { FC, ReactNode } from 'react'

import { useId } from 'react'
import { SPARKLE_PATH } from '@/components/landing/decor/sparkle'

interface GeometricBackdropProps {
    className?: string
}

const round = (value: number): number => Math.round(value * 100) / 100

const TICKS = Array.from({ length: 60 }, (_, index) => {
    const angle = (index * 6 * Math.PI) / 180
    const inner = index % 5 === 0 ? 452 : 462

    return {
        x1: round(800 + Math.cos(angle) * inner),
        y1: round(640 + Math.sin(angle) * inner),
        x2: round(800 + Math.cos(angle) * 472),
        y2: round(640 + Math.sin(angle) * 472)
    }
})

const SPARKS = [
    { x: 236, y: 132, size: 26, gold: true },
    { x: 452, y: 248, size: 14, gold: false },
    { x: 610, y: 96, size: 18, gold: true },
    { x: 1012, y: 168, size: 12, gold: false },
    { x: 1416, y: 420, size: 20, gold: true },
    { x: 118, y: 470, size: 12, gold: false },
    { x: 1500, y: 140, size: 14, gold: false }
]

const PLUSES = [
    { x: 340, y: 340 },
    { x: 702, y: 210 },
    { x: 1130, y: 330 },
    { x: 1330, y: 96 },
    { x: 88, y: 260 },
    { x: 1540, y: 600 },
    { x: 520, y: 520 }
]

// TOP backdrop drawn as clean geometry instead of an illustration: a white summer sky, a sun
// with dashed orbits, a large observation ring behind the character, the outlines of a ruined
// arch and observatory dome, light beams, a + grid and ✦ marks.
const GeometricBackdrop: FC<GeometricBackdropProps> = ({ className }): ReactNode => {
    const id = `geo${useId().replace(/[^a-zA-Z0-9]/g, '')}`
    const sky = `${id}-sky`
    const sun = `${id}-sun`
    const grid = `${id}-grid`
    const fade = `${id}-fade`
    const mask = `${id}-mask`

    return (
        <svg viewBox='0 0 1600 1000' preserveAspectRatio='xMidYMid slice' aria-hidden='true' focusable='false' className={className}>
            <defs>
                <linearGradient id={sky} x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='0%' stopColor='#b9daef' />
                    <stop offset='48%' stopColor='#e3f0f7' />
                    <stop offset='100%' stopColor='#fbfaf6' />
                </linearGradient>
                <radialGradient id={sun}>
                    <stop offset='0%' stopColor='#fff8dc' />
                    <stop offset='45%' stopColor='#fff3c9' stopOpacity='0.85' />
                    <stop offset='100%' stopColor='#fff3c9' stopOpacity='0' />
                </radialGradient>
                <pattern id={grid} width='48' height='48' patternUnits='userSpaceOnUse'>
                    <path d='M20 24H28M24 20V28' stroke='#1d2a33' strokeOpacity='0.14' strokeWidth='1' />
                </pattern>
                <linearGradient id={fade} x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='0%' stopColor='#fff' stopOpacity='0.9' />
                    <stop offset='70%' stopColor='#fff' stopOpacity='0.2' />
                    <stop offset='100%' stopColor='#fff' stopOpacity='0' />
                </linearGradient>
                <mask id={mask}>
                    <rect width='1600' height='1000' fill={`url(#${fade})`} />
                </mask>
            </defs>

            <rect width='1600' height='1000' fill={`url(#${sky})`} />
            <rect width='1600' height='1000' fill={`url(#${grid})`} mask={`url(#${mask})`} />

            <g opacity='0.55'>
                <path d='M-80 0H260L760 1000H420Z' fill='#ffffff' opacity='0.5' />
                <path d='M330 0H430L930 1000H830Z' fill='#ffffff' opacity='0.45' />
                <path d='M520 0H560L1060 1000H1020Z' fill='#ffffff' opacity='0.4' />
            </g>

            <circle cx='1230' cy='230' r='300' fill={`url(#${sun})`} />
            <circle cx='1230' cy='230' r='92' fill='#fffaf0' stroke='#e2b33c' strokeOpacity='0.55' />
            <circle cx='1230' cy='230' r='150' fill='none' stroke='#e2b33c' strokeOpacity='0.45' strokeDasharray='2 8' />
            <circle cx='1230' cy='230' r='206' fill='none' stroke='#e2b33c' strokeOpacity='0.25' />

            <g fill='none' stroke='#1d2a33'>
                <circle cx='800' cy='640' r='380' strokeOpacity='0.1' />
                <circle cx='800' cy='640' r='472' strokeOpacity='0.14' />
                <circle cx='800' cy='640' r='560' strokeOpacity='0.08' strokeDasharray='3 10' />
                <g className='motion-safe:animate-spin-slow' style={{ transformBox: 'view-box', transformOrigin: '800px 640px' }}>
                    {TICKS.map((tick, index) => (
                        <line key={index} x1={tick.x1} y1={tick.y1} x2={tick.x2} y2={tick.y2} strokeOpacity={index % 5 === 0 ? 0.3 : 0.14} />
                    ))}
                </g>
                <path d='M0 760H1600' strokeOpacity='0.12' />
                <path d='M0 800H1600' strokeOpacity='0.07' strokeDasharray='2 8' />
            </g>

            <g fill='#fdfcf8' stroke='#1d2a33' strokeOpacity='0.24' strokeWidth='1.2'>
                <path d='M40 1000V560H92V1000Z' />
                <path d='M92 600H236V640H92Z' />
                <path d='M196 1000V640A94 94 0 0 1 384 640V700H352V650A62 62 0 0 0 228 650V1000Z' />
                <path d='M384 700V1000H352V700Z' />
                <path d='M420 1000V880H520V1000ZM520 1000V920H600V1000Z' />
                <path d='M1180 1000V744H1460V1000Z' />
                <path d='M1196 744A124 124 0 0 1 1444 744Z' />
                <path d='M1312 626V744M1328 626V744' />
                <path d='M1480 1000V650H1530V1000Z' />
            </g>
            <g fill='none' stroke='#1d2a33' strokeOpacity='0.16'>
                <path d='M52 580V980M66 580V980M80 580V980' />
                <path d='M1210 790H1430M1210 830H1430M1210 870H1430' strokeDasharray='4 6' />
                <path d='M1490 680V980M1504 680V980M1518 680V980' />
            </g>

            {SPARKS.map(spark => (
                <path
                    key={`${spark.x}-${spark.y}`}
                    d={SPARKLE_PATH}
                    fill={spark.gold ? '#e2b33c' : '#ffffff'}
                    transform={`translate(${spark.x - spark.size / 2} ${spark.y - spark.size / 2}) scale(${round(spark.size / 24)})`}
                />
            ))}
            <g stroke='#1d2a33' strokeOpacity='0.32' strokeWidth='1.2'>
                {PLUSES.map(item => (
                    <path key={`${item.x}-${item.y}`} d={`M${item.x - 7} ${item.y}H${item.x + 7}M${item.x} ${item.y - 7}V${item.y + 7}`} />
                ))}
            </g>
        </svg>
    )
}

export default GeometricBackdrop
